import React from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import CommonToolbarWithSearchBar from './CommonToolbarWithSearchBar';
import * as securitySlice from '../../../data/security.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import securityUtils from '../../../services/securityUtils';
import GroupDataTable from './GroupDataTable';
import GroupDialog from './GroupDialog';
import { UserGroup } from '../../../data/models/user-group.model';
import { ApplicationTitle } from '../../../data/models/application-title.model';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import {
  Permission,
  PermissionAction,
  PermissionResource,
  PermissionScope,
} from '../../../data/models/permission.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { GridSortModel } from '@mui/x-data-grid-pro';
const PREFIX = 'Groups';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(() => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
}));

enum GroupExportColumn {
  Name = 'Group Name',
  Description = 'Description',
  Permissions = 'Permissions',
  Application = 'Application',
  UserCategory = 'User Category',
  IsActive = 'Is Active',
}

interface GroupExportRow {
  [GroupExportColumn.Name]: string;
  [GroupExportColumn.Description]: string;
  [GroupExportColumn.Permissions]: string;
  [GroupExportColumn.Application]: string;
  [GroupExportColumn.UserCategory]: string;
  [GroupExportColumn.IsActive]: string;
}

function mapGroupToExport(
  group: UserGroup,
  applications: ApplicationTitle[],
  categoryTypes: UserCategoryType[],
  permissions: Permission[],
  resources: PermissionResource[],
  actions: PermissionAction[],
  scopes: PermissionScope[],
): GroupExportRow {
  const applicationObj = applications.find(
    (app) => app.applicationTitleID === group.applicationTitleID,
  );
  const application = applicationObj?.applicationName ?? '';

  const categoryTypeObj = categoryTypes.find(
    (ct) => ct.userCategoryTypeID === group.userCategoryTypeID,
  );
  const categoryType = categoryTypeObj?.userCategoryTypeName ?? '';

  const permissionNames = (group.userPermissions ?? [])
    .map((perm) => {
      const permissionObj = permissions.find(
        (p) => p.userPermissionID === perm.userPermissionID,
      );
      return permissionObj
        ? securityUtils.getPermissionName(
            permissionObj,
            resources,
            actions,
            scopes,
          )
        : '';
    })
    .filter(Boolean)
    .join(', ');
  return {
    [GroupExportColumn.Name]: group.userGroupBC ?? '',
    [GroupExportColumn.Description]: group.description ?? '',
    [GroupExportColumn.Permissions]: permissionNames || 'nothing',
    [GroupExportColumn.Application]: application,
    [GroupExportColumn.UserCategory]: categoryType,
    [GroupExportColumn.IsActive]:
      group.userGroupState === 'active' ? 'True' : 'False',
  };
}

export default function Groups() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const readOnly = false;
  const isSuperReaderUser = useIsSuperReaderUser();

  const loadingUserGroups = useAppSelector(
    securitySlice.selectLoadingUserGroups,
  );
  const userGroups = useAppSelector(securitySlice.selectUserGroups);
  const permissions = useAppSelector(securitySlice.selectUserPermissions);
  const applications = useAppSelector(lookupSlice.selectApplicationTitles);
  const categoryTypes = useAppSelector(lookupSlice.selectUserCategoryTypes);
  const resources = useAppSelector(lookupSlice.selectPermissionResources);
  const actions = useAppSelector(lookupSlice.selectPermissionActions);
  const scopes = useAppSelector(lookupSlice.selectPermissionScopes);
  const loadingAllGroups = useAppSelector(securitySlice.selectLoadingAllGroups);
  const loadedAllGroups = useAppSelector(securitySlice.selectLoadedAllGroups);
  const allGroups = useAppSelector(securitySlice.selectAllGroups);

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState('');
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'description', sort: 'asc' },
  ]);

  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = React.useState('');
  const [groupToDelete, setGroupToDelete] = React.useState<UserGroup>();

  const [group, setGroup] = React.useState<UserGroup>();
  const sortModelMemo = React.useMemo(() => {
    const sort = sortModel[0];
    return sort
      ? { column: sort.field, direction: sort.sort as 'asc' | 'desc' }
      : null;
  }, [sortModel]);

  React.useEffect(() => {
    const sort = sortModel[0]
      ? {
          column: sortModel[0].field,
          direction: sortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    dispatch(securitySlice.fetchUserGroupsAsync(searchText, sort));
  }, [dispatch, searchText, sortModel]);

  React.useEffect(() => {
    dispatch(securitySlice.fetchAllGroupsForExportAsync('', sortModelMemo));
  }, [dispatch, sortModelMemo]);

  React.useEffect(() => {
    dispatch(securitySlice.fetchUserPermissionsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('applicationTitle'));
    dispatch(lookupSlice.fetchLookupItemsAsync('userCategoryType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('permissionResource'));
    dispatch(lookupSlice.fetchLookupItemsAsync('permissionAction'));
    dispatch(lookupSlice.fetchLookupItemsAsync('permissionScope'));
  }, [dispatch]);

  const addGroup = () => {
    setGroup(undefined);
    setEditorOpen(true);
  };

  const editGroup = (id: number) => {
    const group = userGroups.find((g) => g.userGroupID === id);
    if (group) {
      setGroup(group);
      setEditorOpen(true);
    }
  };

  const deleteGroup = (id: number) => {
    const group = userGroups.find((g) => g.userGroupID === id);
    if (group) {
      const name = group.userGroupName;
      const msg = t('groups.confirmDeleteMessage', { name });
      setConfirmDeleteMessage(msg);
      setGroupToDelete(group);
      setConfirmDialogOpen(true);
    }
  };

  const confirmDelete = () => {
    if (groupToDelete) {
      dispatch(securitySlice.deleteUserGroupAsync(groupToDelete.userGroupID!));
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const closeGroupEditor = (saved: boolean) => {
    setEditorOpen(false);
    setGroup(undefined);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const exportDisplayedGroups = userGroups.map((group) =>
    mapGroupToExport(
      group,
      applications,
      categoryTypes,
      permissions,
      resources,
      actions,
      scopes,
    ),
  );

  const fetchAllGroups = React.useCallback(() => {
    if (loadedAllGroups && !loadingAllGroups) {
      return (allGroups ?? []).map((group: UserGroup) =>
        mapGroupToExport(
          group,
          applications,
          categoryTypes,
          permissions,
          resources,
          actions,
          scopes,
        ),
      );
    }
    return [];
  }, [
    loadedAllGroups,
    loadingAllGroups,
    allGroups,
    applications,
    categoryTypes,
  ]);

  return (
    <StyledBox className={classes.container}>
      <CommonToolbarWithSearchBar
        label={t('groups.search')}
        placeholder={t('groups.searchPlaceholder')}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        button={readOnly || isSuperReaderUser ? null : t('groups.add')}
        onCreate={addGroup}
        exportDataMap={{
          [t('groups.displayedGroups')]: exportDisplayedGroups,
          [t('groups.allGroups')]: fetchAllGroups,
        }}
        exportFilenamePrefixMap={{
          [t('groups.displayedGroups')]: t('groups.displayedGroups'),
          [t('groups.allGroups')]: t('groups.allGroups'),
        }}
        disabled={loadingAllGroups}
      />
      <GroupDataTable
        groups={userGroups}
        permissions={permissions}
        resources={resources}
        actions={actions}
        scopes={scopes}
        loading={loadingUserGroups}
        readOnly={readOnly}
        onEdit={editGroup}
        onDelete={deleteGroup}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
      />
      <GroupDialog
        open={editorOpen}
        group={group}
        permissions={permissions}
        applications={applications}
        categoryTypes={categoryTypes}
        resources={resources}
        actions={actions}
        scopes={scopes}
        close={closeGroupEditor}
      />
      <ConfirmationDialog
        title={t('groups.deleteGroup')}
        message={confirmDeleteMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
    </StyledBox>
  );
}
